import { ChangeEvent, ReactElement, useEffect, useState } from 'react';

import qs from 'qs';
import { HeaderFiltersType } from 'context/GlobalFiltersContext/initialState';
import { LkpChart } from '../Chart';
import { useBoolean, useDebounce } from 'hooks';
import { ChartSeriesType, ChartsParamsType } from '../types';
import { LkpChartTopFilter } from '../Filters';
import { fetcher } from 'services';
import { ChartComparisonBetweenWeeksType } from './types';
import { isNumber } from 'fp-ts/lib/number';
import { getChartOptions } from './options';
import { getErrorMessageByStatusCode, groupBy } from 'utils';
//import { useParams } from 'react-router-dom';

export interface LkpChartComparisonBetweenWeeksProps {
  headerFilters: HeaderFiltersType;
  inicio?: string;
  fim?: string;
  sistemas?: any[];
  externalData?: any;
  externalLoading?: boolean;
  disableFetch?: boolean;
  retryFetch?: () => void;
}

const HTTP_PATH = '/atividades-por-status-por-periodo';

const DEFAULT_SERIES = [
  { name: 'Programadas', data: [] },
  { name: 'Realizado', data: [] },
  { name: 'Não Realizado', data: [] },
  { name: 'Baixado Automaticamente', data: [] },
  { name: 'Aguardando Aprovação', data: [] },
];

export function LkpChartComparisonBetweenWeeks({
  headerFilters,
  inicio,
  fim,
  sistemas,
  externalData,
  externalLoading = false,
  disableFetch = false,
  retryFetch,
}: LkpChartComparisonBetweenWeeksProps): ReactElement {
  const { value: isLoading, setValue: setLoading } = useBoolean();
  const isLoadingFinal = externalLoading || isLoading;
  const { value: isError, setValue: setIsError } = useBoolean();
  const [errorMessage, setErrorMessage] = useState<string>();
  //const { dateFrom, dateTo } = useParams<{ dateFrom: string; dateTo: string }>();
  const [series, setSeries] = useState<Array<ChartSeriesType>>(DEFAULT_SERIES);
  const [periods, setPeriods] = useState<Array<string>>([]);
  const [params, setParams] = useState<ChartsParamsType>({
    Empresa: headerFilters?.companies?.id || 0,
    Sites: headerFilters?.units?.id,
    Inicio: inicio,
    Fim: fim,
    GrupoSite: headerFilters?.unitGroups?.id || [],
    SubGrupoSite: headerFilters?.unitSubgroups?.id || [],
    Cidade: headerFilters?.cities?.id || [],
    Estado: headerFilters?.states?.id || [],
    Sistemas: sistemas || [],
  });

  const debouncedStartValue = useDebounce<string>(params?.Inicio || '', 500);
  const debouncedEnvValue = useDebounce<string>(params?.Fim || '', 500);
  useEffect(() => {
    setParams(prev => ({
      ...prev,
      Empresa: headerFilters?.companies?.id || 0,
      Sites: headerFilters?.units?.id,
      GrupoSite: headerFilters?.unitGroups?.id || [],
      SubGrupoSite: headerFilters?.unitSubgroups?.id || [],
      Cidade: headerFilters?.cities?.id || [],
      Estado: headerFilters?.states?.id || [],
      Inicio: inicio,
      Fim: fim,
      Sistemas: sistemas,
    }));
  }, [
    inicio,
    fim,
    sistemas,
    headerFilters?.companies?.id,
    headerFilters?.units?.id,
    headerFilters?.unitGroups?.id,
    headerFilters?.unitSubgroups?.id,
    headerFilters?.cities?.id,
    headerFilters?.states?.id,
  ]);

  useEffect(() => {
    if (disableFetch && externalData) {
      const _groupStatus = groupBy<any>(externalData, d => d.status);
      const _series = _groupStatus.map(({ name, data }) => {
        const _qttData: number[] = [];
        data.forEach(({ quantidade }) => {
          const _num = isNumber(Number(quantidade)) ? Number(quantidade) : 0;
          _qttData.push(_num);
        });
        return { name, data: _qttData };
      });
      setSeries(_series);

      const _groupPeriods = groupBy<any>(externalData, d => d.periodo);
      setPeriods(_groupPeriods.map(({ name }) => name));
      return;
    }

    if (disableFetch) return;

    (async () => {
      await fetchChartData();
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedStartValue,
    debouncedEnvValue,
    sistemas,
    headerFilters?.unitGroups?.id,
    headerFilters?.unitSubgroups?.id,
    headerFilters?.companies?.id,
    headerFilters?.states?.id,
    headerFilters?.cities?.id,
    disableFetch,
    externalData,
  ]);

  const fetchChartData = async () => {
    setLoading(true);
    const result = await fetcher
      .get<Array<ChartComparisonBetweenWeeksType>>(HTTP_PATH, 'dashboard', {
        params: {
          ...params,
          GrupoSite: headerFilters?.unitGroups?.id,
          SubGrupoSite: headerFilters?.unitSubgroups?.id,
          Cidade: headerFilters?.cities?.id,
          Estado: headerFilters?.states?.id,
        },
        paramsSerializer: params => qs.stringify(params, { arrayFormat: 'repeat' }),
      })
      .catch(error => {
        const message = getErrorMessageByStatusCode(error, HTTP_PATH);
        setErrorMessage(message);
        setIsError(true);
      });

    if (result) {
      const _groupStatus = groupBy<ChartComparisonBetweenWeeksType>(result, d => d.status);
      const _series = _groupStatus.map(({ name, data }) => {
        const _qttData: number[] = [];
        data.forEach(({ quantidade }) => {
          const _num = isNumber(Number(quantidade)) ? Number(quantidade) : 0;
          _qttData.push(_num);
        });
        return { name, data: _qttData };
      });
      setSeries(_series);

      const _groupPeriods = groupBy<ChartComparisonBetweenWeeksType>(result, d => d.periodo);
      setPeriods(_groupPeriods.map(({ name }) => name));
    }

    setLoading(false);
  };

  const handleChangeStartDate = (event: ChangeEvent<HTMLInputElement>) => {
    const { value: Inicio } = event.target;
    setParams({ ...params, Inicio });
  };

  const handleChangeEndDate = (event: ChangeEvent<HTMLInputElement>) => {
    const { value: Fim } = event.target;
    setParams({ ...params, Fim });
  };

  return (
    <LkpChart
      title="Atividades - Comparativo entre semanas"
      companyId={headerFilters?.companies?.id || 0}
      loading={isLoadingFinal}
      error={isError}
      errorMessage={errorMessage}
      series={series}
      //width={1700}
      onChartTryAgain={disableFetch ? retryFetch : fetchChartData}
      options={getChartOptions(periods, {
        legend: {
          position: 'bottom',
        },
        colors: ['#00C0C7', '#5144D3', '#E8871A', '#DA3490', '#9089FA', '#47E26F', '#2780EB'],
        dataLabels: {
          enabled: true,
          formatter: function () {
            return ``;
          },
        },
      })}
      filter={<LkpChartTopFilter onChangeStartDate={handleChangeStartDate} onChangeEndDate={handleChangeEndDate} />}
    />
  );
}
